import { useState, useCallback } from 'react';

/**
 * Custom hook to manage quote comparison functionality
 * Handles selection, deselection, and comparison actions
 */
const useCompareQuotes = () => {
  const [compareSelections, setCompareSelections] = useState({});
  const [compareModalData, setCompareModalData] = useState(null);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);

  /**
   * Toggle quote selection for comparison
   */
  const toggleQuoteSelection = useCallback((productType, quoteId) => {
    setCompareSelections(prev => {
      const currentSelections = prev[productType] || [];
      const isSelected = currentSelections.includes(quoteId);

      if (isSelected) {
        // Remove from selection
        return {
          ...prev,
          [productType]: currentSelections.filter(id => id !== quoteId)
        };
      } else {
        // Add to selection (max 4 quotes per product type)
        if (currentSelections.length >= 4) {
          return prev; // Don't add if already at max
        }
        return {
          ...prev,
          [productType]: [...currentSelections, quoteId]
        };
      }
    });
  }, []);

  /**
   * Check if a quote is selected for comparison
   */
  const isQuoteSelected = useCallback(
    (productType, quoteId) => {
      return (compareSelections[productType] || []).includes(quoteId);
    },
    [compareSelections]
  );

  /**
   * Get count of selected quotes for a product type
   */
  const getSelectionCount = useCallback(
    productType => {
      return (compareSelections[productType] || []).length;
    },
    [compareSelections]
  );

  /**
   * Get total count of all selected quotes
   */
  const getTotalSelectionCount = useCallback(() => {
    return Object.values(compareSelections).reduce((total, arr) => total + arr.length, 0);
  }, [compareSelections]);

  /**
   * Check if can add more quotes to comparison for a product type
   */
  const canAddMore = useCallback(
    productType => {
      return getSelectionCount(productType) < 4;
    },
    [getSelectionCount]
  );

  /**
   * Clear selections for a specific product type
   */
  const clearProductTypeSelections = useCallback(productType => {
    setCompareSelections(prev => ({
      ...prev,
      [productType]: []
    }));
  }, []);

  /**
   * Clear all selections
   */
  const clearAllSelections = useCallback(() => {
    setCompareSelections({});
  }, []);

  /**
   * Open compare modal with selected quotes
   */
  const openCompareModal = useCallback((quotes, productType) => {
    setCompareModalData({
      quotes,
      productType
    });
    setIsCompareModalOpen(true);
  }, []);

  /**
   * Close compare modal
   */
  const closeCompareModal = useCallback(() => {
    setIsCompareModalOpen(false);
    setCompareModalData(null);
  }, []);

  /**
   * Get selected quotes for a product type
   */
  const getSelectedQuotes = useCallback(
    (productType, allQuotes) => {
      const selectedIds = compareSelections[productType] || [];
      return allQuotes.filter(quote => selectedIds.includes(quote.id || quote.quote_id));
    },
    [compareSelections]
  );

  return {
    // State
    compareSelections,
    compareModalData,
    isCompareModalOpen,

    // Computed
    totalSelectionCount: getTotalSelectionCount(),

    // Actions
    toggleQuoteSelection,
    isQuoteSelected,
    getSelectionCount,
    getTotalSelectionCount,
    canAddMore,
    clearProductTypeSelections,
    clearAllSelections,
    openCompareModal,
    closeCompareModal,
    getSelectedQuotes,

    // State setters
    setCompareSelections,
    setCompareModalData,
    setIsCompareModalOpen
  };
};

export default useCompareQuotes;
